import { c as _c } from "react/compiler-runtime";
import React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Label } from "@/components/ui/form/Label";
const checkboxVariants = cva("appearance-none w-5 h-5 border-2 rounded-xs transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed outline-offset-0 focus-visible:outline-2 focus-visible:outline-offset-2 flex items-center justify-center shrink-0", {
  variants: {
    color: {
      purple: "border-purple checked:bg-purple hover:not-checked:bg-purple/20 focus-visible:outline-purple",
      yellow: "border-yellow checked:bg-yellow hover:not-checked:bg-yellow/20 focus-visible:outline-yellow",
      dark: "border-white/20 checked:bg-yellow checked:border-yellow hover:not-checked:border-yellow/60 focus-visible:outline-yellow"
    }
  },
  defaultVariants: {
    color: "purple"
  }
});
export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "color">, VariantProps<typeof checkboxVariants> {
  /** Error message displayed below the checkbox */
  error?: string;
  /** Label for the checkbox */
  label?: string | React.ReactNode;
}

/**
 * Checkbox component for boolean input.
 * Supports color variants, labels, and error states.
 */
export const Checkbox: React.FC<CheckboxProps> = t0 => {
  const $ = _c(33);
  let className;
  let error;
  let id;
  let label;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      color: t1,
      error,
      label,
      className,
      id,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = error;
    $[3] = id;
    $[4] = label;
    $[5] = props;
    $[6] = t1;
  } else {
    className = $[1];
    error = $[2];
    id = $[3];
    label = $[4];
    props = $[5];
    t1 = $[6];
  }
  const color = t1 === undefined ? "purple" : t1;
  const checkboxId = id || props.name;
  let t2;
  if ($[7] !== className || $[8] !== color) {
    t2 = checkboxVariants({
      color,
      className
    });
    $[7] = className;
    $[8] = color;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== checkboxId || $[11] !== props || $[12] !== t2) {
    t3 = <input {...props} type="checkbox" id={checkboxId} className={t2} />;
    $[10] = checkboxId;
    $[11] = props;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const t4 = `absolute w-3 h-3 pointer-events-none hidden [input:checked+&]:block ${color === "purple" ? "text-white" : "text-black"}`;
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <polyline points="20 6 9 17 4 12" />;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t4) {
    t6 = <svg className={t4} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">{t5}</svg>;
    $[15] = t4;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t3 || $[18] !== t6) {
    t7 = <div className="relative flex items-center justify-center">{t3}{t6}</div>;
    $[17] = t3;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== checkboxId || $[21] !== color || $[22] !== label || $[23] !== props) {
    t8 = label && <Label htmlFor={checkboxId} color={color} className="mb-0 cursor-pointer select-none">{label}{props.required && <span className="ml-1 text-red-500">*</span>}</Label>;
    $[20] = checkboxId;
    $[21] = color;
    $[22] = label;
    $[23] = props;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== t7 || $[26] !== t8) {
    t9 = <div className="flex items-center gap-sm">{t7}{t8}</div>;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== error) {
    t10 = error && <span className="text-red-500 text-sm">{error}</span>;
    $[28] = error;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10 || $[31] !== t9) {
    t11 = <div className="flex flex-col gap-xs w-fit">{t9}{t10}</div>;
    $[30] = t10;
    $[31] = t9;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  return t11;
};